/**
 * Talks to the challenge API (worker/challenges.ts) and remembers the
 * challenge this page was opened with, so the game-over dialog can say how
 * the player did against it.
 */

import type { GameSlug } from '../game-slugs';
import { toChallenge, type Challenge, type ChallengeRun } from './challenge-rules';

let active: Challenge | null = null;
const listeners = new Set<(challenge: Challenge | null) => void>();

/** The challenge being played on this page, if it is for `game`. */
export function activeChallenge(game: GameSlug): Challenge | null {
  return active && active.game === game ? active : null;
}

export function setActiveChallenge(challenge: Challenge | null): void {
  active = challenge;
  listeners.forEach((listener) => listener(challenge));
}

export function onActiveChallenge(listener: (challenge: Challenge | null) => void): void {
  listeners.add(listener);
}

/** The link to share: freecasual.games/c/<id>. */
export function challengeLink(id: string): string {
  return `${window.location.origin}/c/${id}`;
}

export async function fetchChallenge(id: string): Promise<Challenge | null> {
  try {
    const response = await fetch(`/api/challenges/${encodeURIComponent(id)}`);
    if (!response.ok) return null;
    return toChallenge(await response.json());
  } catch {
    return null;
  }
}

/** Stores the run as a challenge and returns its id. Rejects with a message to show. */
export async function createChallenge(run: ChallengeRun, parent?: string): Promise<string> {
  let response: Response;
  try {
    response = await fetch('/api/challenges', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...run, ...(parent ? { parent } : {}) }),
    });
  } catch {
    throw new Error('Could not make the challenge link. Check your connection.');
  }
  const body: unknown = await response.json().catch(() => null);
  if (response.ok && typeof body === 'object' && body !== null && 'id' in body && typeof body.id === 'string') return body.id;
  const error = typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string' ? body.error : null;
  throw new Error(error ?? 'Could not make the challenge link. Try again.');
}
